import type { InvitaeFormState } from '../formData'
import { TEST_PANELS, SPECIMEN_TYPES, PRIMARY_INDICATIONS, CANCER_TYPES } from '../formData'
import { LAB_ACCOUNT_SYSTEMS, INVITAE_PREFERRED_COLLECTION_KIT } from '../formData'
import type { OrderPdfData, OrderPdfTest, OrderPdfSection, AutoPopulateData } from './pdfTypes'

export function mapInvitaeCancerToPdf(
  form: InvitaeFormState,
  auto: AutoPopulateData,
): OrderPdfData {
  // Resolve selected test panels
  const allTests = TEST_PANELS.flatMap((cat) => cat.tests)
  const selectedTests: OrderPdfTest[] = form.selectedTests
    .map((code) => {
      const found = allTests.find((t) => t.code === code)
      if (!found) return null
      const category = TEST_PANELS.find((cat) => cat.tests.some((t) => t.code === code))?.category
      return { code: found.code, name: found.name, category, geneList: found.geneList }
    })
    .filter((t): t is OrderPdfTest => t !== null)

  if (form.individualGenes) {
    selectedTests.push({
      code: 'CUSTOM',
      name: `Individual Genes: ${form.individualGenes}`,
      category: 'Custom',
    })
  }

  const vendorSections: OrderPdfSection[] = []

  // Kit shipping — Invitae ships the collection kit to the patient
  const shipAddress = form.kitShippingAddress || auto.patient.address
  const shipCityStateZip = form.kitShippingCityStateZip || auto.patient.cityStateZip
  const kitShippingData: Record<string, string> = {}
  if (selectedTests.length > 0) kitShippingData['Collection Kit'] = INVITAE_PREFERRED_COLLECTION_KIT
  if (shipAddress) kitShippingData['Shipping Address'] = shipAddress
  if (shipCityStateZip) kitShippingData['City, State, ZIP'] = shipCityStateZip
  if (form.kitShippingPhoneType) kitShippingData['Phone Type'] = form.kitShippingPhoneType
  if (form.kitShippingLanguagePreference)
    kitShippingData['Language Preference'] = form.kitShippingLanguagePreference
  if (Object.keys(kitShippingData).length > 0) {
    vendorSections.push({
      title: 'Kit Shipping Information',
      type: 'keyValue',
      data: kitShippingData,
    })
  }

  // Specimen
  const specimenLabel =
    SPECIMEN_TYPES.find((s) => s.value === form.specimenType)?.label || form.specimenType
  const specimenData: Record<string, string> = {}
  if (specimenLabel) specimenData['Specimen Type'] = specimenLabel
  if (form.dnaSource) specimenData['DNA Source'] = form.dnaSource
  if (form.specimenCollectionDate) specimenData['Collection Date'] = form.specimenCollectionDate
  if (Object.keys(specimenData).length > 0) {
    vendorSections.push({ title: 'Specimen Information', type: 'keyValue', data: specimenData })
  }

  // Primary indication
  const indication =
    PRIMARY_INDICATIONS.find((i) => i.value === form.primaryIndication)?.label ||
    form.primaryIndication
  if (indication) {
    const indicationData: Record<string, string> = { 'Primary Indication': indication }
    if (form.primaryIndicationOther) indicationData['Other Detail'] = form.primaryIndicationOther
    vendorSections.push({ title: 'Clinical Indication', type: 'keyValue', data: indicationData })
  }

  // Personal cancer history
  if (!form.noPersonalHistory && form.cancerHistory.length > 0) {
    const rows = form.cancerHistory.map((entry) => {
      const typeLabel = CANCER_TYPES.find((c) => c.value === entry.type)?.label || entry.type
      const details: string[] = []
      if (entry.breastOptions.length > 0) details.push(entry.breastOptions.join(', '))
      if (entry.msiHighOrIhcAbnormal) details.push(`MSI: ${entry.msiResult || 'Yes'}`)
      if (entry.additionalInfo) details.push(entry.additionalInfo)
      if (entry.metastatic) details.push(`Metastatic: ${entry.metastatic}`)
      if (entry.gleasonScore) details.push(`Gleason: ${entry.gleasonScore}`)
      if (entry.polypCount) details.push(`Polyps: ${entry.polypCount}`)
      if (entry.pathology) details.push(`Pathology: ${entry.pathology}`)
      if (entry.cancerType) details.push(`Type: ${entry.cancerType}`)
      return [typeLabel, entry.ageOfDiagnosis || 'N/A', details.join('; ') || '-']
    })
    vendorSections.push({
      title: 'Personal Cancer History',
      type: 'table',
      headers: ['Cancer Type', 'Age at Dx', 'Details'],
      rows,
    })
  } else if (form.noPersonalHistory) {
    vendorSections.push({
      title: 'Personal Cancer History',
      type: 'text',
      content: 'No personal cancer history reported.',
    })
  }

  // Family history
  if (!form.noFamilyHistory && form.familyHistory.length > 0) {
    const famRows = form.familyHistory
      .filter((r) => r.relationship || r.condition)
      .map((r) => [
        r.relationship,
        r.maternalPaternal || '-',
        r.condition,
        r.ageAtDiagnosis || 'N/A',
        r.additionalDetails || '-',
      ])
    if (famRows.length > 0) {
      vendorSections.push({
        title: 'Family History',
        type: 'table',
        headers: ['Relationship', 'Side', 'Condition', 'Age at Dx', 'Details'],
        rows: famRows,
      })
    }
  } else {
    const famText = form.patientIsAdopted
      ? 'Patient is adopted. Family history unavailable.'
      : 'No family history reported.'
    if (form.noFamilyHistory || form.patientIsAdopted) {
      vendorSections.push({ title: 'Family History', type: 'text', content: famText })
    }
  }

  // Race/ethnicity
  if (form.raceEthnicity.length > 0) {
    vendorSections.push({ title: 'Race/Ethnicity', type: 'list', items: form.raceEthnicity })
  }

  return {
    vendorName: 'Invitae (Labcorp Genetics)',
    orderTitle: 'Hereditary Cancer Requisition',
    patient: {
      firstName: auto.patient.firstName,
      middleName: auto.patient.middleName,
      lastName: auto.patient.lastName,
      dateOfBirth: auto.patient.dateOfBirth,
      sex: auto.patient.sexCode,
      email: auto.patient.email,
      phone: auto.patient.phone,
      address: auto.patient.address,
      city: auto.patient.city,
      state: auto.patient.state,
      zip: auto.patient.zip,
      country: auto.patient.country,
      patientId: auto.patient.patientId,
    },
    provider: {
      organizationName: auto.provider.organizationName,
      providerName: auto.provider.providerName,
      npi: auto.provider.npi,
      phone: auto.provider.orgPhone,
      fax: auto.provider.orgFax,
      address: auto.provider.orgAddress,
      city: auto.provider.orgCity,
      state: auto.provider.orgState,
      zip: auto.provider.orgZip,
      email: auto.provider.providerEmail,
      accountNumber: auto.provider.labAccountNumbers[LAB_ACCOUNT_SYSTEMS.INVITAE] || '',
    },
    insurance: {
      primaryCarrier: auto.insurance.primaryCarrier,
      primaryMemberId: auto.insurance.primaryMemberId,
      primaryGroup: auto.insurance.primaryGroup,
      policyholderName: auto.insurance.primaryCarrier ? auto.insurance.policyholderName : '',
      relationship: auto.insurance.primaryCarrier ? auto.insurance.relationship : '',
    },
    icd10Codes: form.diagnosisText
      ? form.diagnosisText
          .split(/[;\n]/)
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    selectedTests,
    vendorSections,
    signatureDataUrl: form.signatureDataUrl,
    signatureDate: form.signatureDate,
  }
}
